import { useState, useEffect } from 'react';
import { formatNumber } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';

interface PortalNotification {
  id: string;
  title: string;
  message: string;
  type: string;
  icon: string;
  link_url: string | null;
  link_text: string | null;
  is_active: boolean;
  is_dismissible: boolean;
  start_date: string;
  end_date: string | null;
  target_country_codes: string[] | null;
  target_user_ids: string[] | null;
  target_permissions: string[] | null;
  poll_options: Array<{ label: string; emoji: string }> | null;
  poll_ends_at: string | null;
  poll_show_results: boolean;
  [key: string]: unknown;
}

interface PollResults {
  counts: number[];
  total: number;
}

interface UseNotificationsReturn {
  notifications: PortalNotification[];
  dismissNotification: (notificationId: string) => Promise<void>;
  voteOnPoll: (notificationId: string, selectedOption: number) => Promise<void>;
  hasVoted: (notificationId: string) => boolean;
  getUserVote: (notificationId: string) => number | undefined;
  getPollResults: (notificationId: string) => PollResults | undefined;
  loadPollResults: (notificationId: string) => Promise<void>;
  loading: boolean;
}

export default function useNotifications(): UseNotificationsReturn {
  const { supabase, user, hasPermission } = useAuth();
  const { countryCode } = useConfig();
  const [notifications, setNotifications] = useState<PortalNotification[]>([]);
  const [userVotes, setUserVotes] = useState<Record<string, number>>({});
  const [pollResults, setPollResults] = useState<Record<string, PollResults>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase || !user) return;
    loadNotifications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabase, user]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const now = new Date().toISOString();

      const [notifRes, dismissRes, votesRes] = await Promise.all([
        supabase
          .from('portal_notifications')
          .select('*')
          .eq('is_active', true)
          .lte('start_date', now)
          .or(`end_date.is.null,end_date.gte.${now}`)
          .order('created_at', { ascending: false }),
        supabase
          .from('portal_notification_dismissals')
          .select('notification_id')
          .eq('user_id', user?.id),
        supabase
          .from('notification_poll_votes')
          .select('notification_id, selected_option')
          .eq('user_id', user?.id)
      ]);

      if (notifRes.error) throw notifRes.error;

      const dismissed = new Set<string>(
        (dismissRes.data || []).map((d: { notification_id: string }) => d.notification_id)
      );

      const votes: Record<string, number> = {};
      (votesRes.data || []).forEach((v: { notification_id: string; selected_option: number }) => {
        votes[v.notification_id] = v.selected_option;
      });
      setUserVotes(votes);

      const visible = (notifRes.data || []).filter((n: PortalNotification) => {
        if (dismissed.has(n.id)) return false;
        if (n.target_country_codes?.length && n.target_country_codes.length > 0) {
          if (!n.target_country_codes.includes(countryCode)) return false;
        }
        if (n.target_user_ids?.length && n.target_user_ids.length > 0) {
          if (!n.target_user_ids.includes(user?.id || '')) return false;
        }
        if (n.target_permissions?.length && n.target_permissions.length > 0) {
          const hasAny = n.target_permissions.some((perm: string) => {
            const parts = perm.split('.');
            return hasPermission(parts[0] || '', (parts[1] || 'view') as 'view' | 'contribute' | 'manage');
          });
          if (!hasAny) return false;
        }
        return true;
      });

      // Derived: synthesize a non-dismissible banner per unpaid Licensor bill.
      // DK-only for now, gated on payouts.view. Persists naturally — once the
      // bill goes paid it drops out of the query and the banner disappears.
      const derived = await loadLicensorBillNotifications();

      // Derived: nudge unconnected users to link Telegram. The only way to
      // make it disappear is to actually link — the next page load sees the
      // chat_id and the banner vanishes. No dismiss button, no nagging once
      // they've done it.
      const telegram = await loadTelegramConnectNotification();

      setNotifications([...telegram, ...derived, ...visible] as PortalNotification[]);
    } catch (err) {
      console.error('[Notifications] Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadLicensorBillNotifications = async (): Promise<PortalNotification[]> => {
    // ConfigContext returns countryCode as 'DK'/'ZA' (uppercase). Normalise.
    if ((countryCode || '').toLowerCase() !== 'dk') return [];
    if (!hasPermission('payouts', 'view')) return [];
    try {
      const { data: vendor } = await supabase
        .from('vendors')
        .select('id, currency_code')
        .eq('name', 'CleanMyBed Limited')
        .maybeSingle();
      if (!vendor) return [];
      const { data: bills } = await supabase
        .from('bills')
        .select('id, bill_number, balance_due, due_date, bill_date')
        .eq('vendor_id', (vendor as { id: string }).id)
        .in('status', ['draft', 'open', 'processing'])
        .order('bill_date', { ascending: false });

      const currency = (vendor as { currency_code: string | null }).currency_code || 'DKK';
      return (bills || []).map((b: { id: string; bill_number: string | null; balance_due: number | string; due_date: string | null; bill_date: string | null }) => {
        const amount = formatNumber(Number(b.balance_due || 0), 'en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const dueLine = b.due_date ? ` · due ${b.due_date}` : '';
        return {
          id: `licensor-bill-${b.id}`,
          title: 'Licensor payout pending',
          message: `${b.bill_number || 'Bill'} · ${currency} ${amount}${dueLine}`,
          type: 'warning',
          icon: '💷',
          link_url: '/payouts',
          link_text: 'Open Payouts',
          is_active: true,
          is_dismissible: false,
          start_date: b.bill_date || new Date().toISOString(),
          end_date: null,
          target_country_codes: ['dk'],
          target_user_ids: null,
          target_permissions: null,
          poll_options: null,
          poll_ends_at: null,
          poll_show_results: false,
        } satisfies PortalNotification;
      });
    } catch (err) {
      console.error('[Notifications] Licensor bill query error:', err);
      return [];
    }
  };

  const loadTelegramConnectNotification = async (): Promise<PortalNotification[]> => {
    if (!user?.id) return [];
    try {
      const { data: link } = await supabase
        .from('user_telegram_links')
        .select('telegram_chat_id')
        .eq('user_id', user.id)
        .eq('bot_type', 'leads')
        .maybeSingle();

      // Hide once a real chat ID has been stamped on the leads row.
      const linked = !!(link && (link as { telegram_chat_id?: string | null }).telegram_chat_id);
      if (linked) return [];

      return [{
        id: 'telegram-connect',
        title: 'Connect your Telegram',
        message: 'Get a Telegram message the moment a booking is assigned to you. Takes 20 seconds.',
        type: 'info',
        icon: '📲',
        link_url: '/profile',
        link_text: 'Open Profile',
        is_active: true,
        is_dismissible: false,
        start_date: new Date().toISOString(),
        end_date: null,
        target_country_codes: null,
        target_user_ids: null,
        target_permissions: null,
        poll_options: null,
        poll_ends_at: null,
        poll_show_results: false,
      } satisfies PortalNotification];
    } catch (err) {
      console.error('[Notifications] Telegram-connect check error:', err);
      return [];
    }
  };

  const dismissNotification = async (notificationId: string) => {
    try {
      setNotifications(prev => prev.filter(n => n.id !== notificationId));

      await supabase
        .from('portal_notification_dismissals')
        .upsert({
          user_id: user?.id,
          notification_id: notificationId,
          dismissed_at: new Date().toISOString()
        }, { onConflict: 'user_id,notification_id' });
    } catch (err) {
      console.error('[Notifications] Dismiss error:', err);
      loadNotifications();
    }
  };

  const voteOnPoll = async (notificationId: string, selectedOption: number) => {
    try {
      setUserVotes(prev => ({ ...prev, [notificationId]: selectedOption }));

      const { error } = await supabase
        .from('notification_poll_votes')
        .upsert({
          notification_id: notificationId,
          user_id: user?.id,
          selected_option: selectedOption
        }, { onConflict: 'notification_id,user_id' });

      if (error) throw error;
      await loadPollResults(notificationId);
    } catch (err) {
      console.error('[Notifications] Vote error:', err);
      setUserVotes(prev => {
        const updated = { ...prev };
        delete updated[notificationId];
        return updated;
      });
    }
  };

  const loadPollResults = async (notificationId: string) => {
    try {
      const { data, error } = await supabase
        .from('notification_poll_votes')
        .select('selected_option')
        .eq('notification_id', notificationId);

      if (error) throw error;

      const notification = notifications.find(n => n.id === notificationId);
      if (!notification?.poll_options) return;

      const counts = notification.poll_options.map((_: unknown, idx: number) =>
        (data || []).filter((v: { selected_option: number }) => v.selected_option === idx).length
      );

      setPollResults(prev => ({
        ...prev,
        [notificationId]: { counts, total: data?.length || 0 }
      }));
    } catch (err) {
      console.error('[Notifications] Load poll results error:', err);
    }
  };

  const hasVoted = (notificationId: string): boolean => userVotes[notificationId] !== undefined;
  const getUserVote = (notificationId: string): number | undefined => userVotes[notificationId];
  const getPollResults = (notificationId: string): PollResults | undefined => pollResults[notificationId];

  return {
    notifications,
    dismissNotification,
    voteOnPoll,
    hasVoted,
    getUserVote,
    getPollResults,
    loadPollResults,
    loading,
  };
}
